<template>
  <div class="clip-wrapper">
    <div class="title">
      <scissors-icon/>
      <span>{{ $t('New clip') }}</span>
    </div>
    <section class="clip-ruler">
      <clipping-time-ruller
        :duration="duration"
        :currentTime="startTime"
        :playingTime="playingTime"
        :minDuration="clipDuration.min"
        :maxDuration="clipDuration.max"
        :visibleDuration="visibleDuration"
        @start-drag="handleStartDrag"
        @clip-change="handleClipChange"
        @play-clip="onClipPlay"
      />
    </section>
    <section class="d-flex gap-05 justify-content-center skip-buttons">
      <button-component
        v-for="button in skipButtons"
        :key="button.key"
        variant="bordered"
        @btn-click="button.action"
        :title="button.title"
      >
        <component :is="button.icon" :time="button.time"/>
      </button-component>
    </section>

    <section class="clip-quick">
      <div class="section-clip-title">
        <span>
          {{ $t('CLIP.tag') }}
        </span>
        <span class="placeholder-text">{{ $t('ATTRIBUTE.required') }}</span>
      </div>
      <div class="quick-names">
        <button-component
          v-for="tagName in buttonTags"
          :title="tagName"
          :key="tagName"
          variant="bordered"
          :class="{'selected': tag === tagName}"
          @btn-click="updateTag(tagName)"
        />
      </div>
      <div class="clip-error" v-if="!tag && showDisabledError">
        {{ $t('ERRORS.selectTag') }}
      </div>
    </section>
    <section class="clip-manual">
      <div class="clip-name">
        <label for="clip-name">{{ $t('CLIP.name') }}</label>
        <input
          v-model="name"
          id="clip-name"
          class="form-control"
          :placeholder="$t('PLACEHOLDER.Required')"
        />
      </div>
      <div v-if="!name.length && showDisabledError" class="clip-error">
        {{ $t('ERRORS.emptyName') }}
      </div>
      <div v-if="otherError" class="clip-error">
        {{ $t('ERRORS.somethingWrong') }}
      </div>
    </section>
    <section>
      <button-component
        @btn-click="createClip"
        @disabled-click="displayError"
        title="CLIP.cut"
        :disabled="!canCut"
      />
    </section>
    <created-clips
      :event="event"
      class="created-clips"
      @playClip="onClipPlay"
    />
    <tutorial-caller name="clipping"/>
  </div>
</template>

<script>
import {PLAYER_ACTIONS, PLAYER_EVENTS} from '@/const';

import ButtonComponent from '@/components/base-components/buttons/ButtonComponent';
import {timeToHumanFormat} from '@/utils/helpers';
import ScissorsIcon from '@/assets/img/ScissorsIcon';
import {SEND_CLIP} from '@/store/actionTypes';
import {mapActions, mapMutations} from 'vuex';

import {ADD_TOAST} from '@/store/mutationTypes';
import VisaicV3Analytics from '@/components/base-components/Analytics/VisaicV3Analytics';
import TutorialCaller from '@/components/onboarding/TutorialCaller.vue';
import ClippingTimeRuller from './ClippingTimeRuller.vue';
import CreatedClips from './CreatedClips.vue';

import {mapState} from 'vuex';
import RewindButtonIcon from '@/assets/img/rewindButtonIcon.vue';
import FlashForwardButtonIcon from '@/assets/img/flashForwardButtonIcon.vue';

export default {
  name: 'ClipCutComponent',
  components: {
    FlashForwardButtonIcon,
    RewindButtonIcon,
    TutorialCaller,
    ScissorsIcon,
    ButtonComponent,
    ClippingTimeRuller,
    CreatedClips,
  },
  props: {
    playerInterface: {
      type: Object,
      required: true,
    },
    videoPlay: {
      type: Boolean,
      default: false,
    },
    event: {
      type: Object,
      required: true,
    },
    sportType: {
      type: String,
      default: 'default',
    },
    onClipCreatRequested: {
      type: Function,
    },
  },
  data() {
    return {
      player: null,
      startTime: 0,
      clipStartTime: 0,
      playingTime: 0,
      isDragging: false,
      name: '',
      selectedDuration: 30,
      duration: 0,
      showDisabledError: false,
      otherError: false,
      tag: null,
      playPartAbortCb: () => {},
      handleClipChangeHistory: {},
    };
  },
  rewindButtons: [
    {
      time: 10,
      icon: RewindButtonIcon,
    },
    {
      time: 5,
      icon: RewindButtonIcon,
    },
    {
      time: 1,
      icon: RewindButtonIcon,
    },
  ],
  flashForwardButtons: [
    {
      time: 1,
      icon: FlashForwardButtonIcon,
    },
    {
      time: 5,
      icon: FlashForwardButtonIcon,
    },
    {
      time: 10,
      icon: FlashForwardButtonIcon,
    },
  ],
  methods: {
    ...mapActions({
      sendClip: SEND_CLIP,
    }),
    ...mapMutations({
      addToast: ADD_TOAST,
    }),
    getPlayerTime() {
      return Math.floor(this.playerInterface?.[PLAYER_ACTIONS.getTime]?.());
    },
    onClipPlay({
      start,
      end,
    } = {}) {
      this.playPartAbortCb?.();
      this.playPartAbortCb = this.playerInterface?.[PLAYER_ACTIONS.playPart]?.({
        start,
        end,
      });
    },
    updateTag(name) {
      this.tag = name;
    },
    setInitData() {
      this.name = '';
      this.tag = null;
    },
    createClip() {
      this.sendClip({
        event: this.$route.params.id,
        name: this.name,
        start: timeToHumanFormat(this.clipStartTime, true),
        duration: timeToHumanFormat(this.selectedDuration, true),
        tags: [this.tag],
      }).then(() => {
        this.setInitData();
        VisaicV3Analytics.event({
          event: 'clip_create',
          clip_id: this.$route.params.id,
        });
      }).catch(() => {
        this.otherError = true;
      }).finally(() => {
        this.onClipCreatRequested?.();
      });

      this.addToast({
        text: `CLIP.Hang`,
        time: 15000,
        type: 'success',
      });
    },
    displayError(display = true) {
      this.showDisabledError = display;
    },
    updateDuration() {
      this.duration = Math.floor(this.playerInterface?.[PLAYER_ACTIONS.totalTime]?.());
    },
    handleClipChange({startPosition, duration}) {
      this.playPartAbortCb?.();
      const {startPosition: prevStart} = this.handleClipChangeHistory;

      const newPosition = startPosition !== prevStart?
        startPosition :
        startPosition + duration;

      this.$nextTick(() => {
        this.player?.[PLAYER_ACTIONS.seek]?.(newPosition);
      });

      this.handleClipChangeHistory = {
        startPosition,
        duration,
      };

      this.selectedDuration = duration;
      this.clipStartTime = startPosition;
    },
    handleStartDrag() {
      this.isDragging = true;
    },
    startSeekPlayer({offset}) {
      if (!this.isDragging) {
        this.clipStartTime = offset;
        this.startTime = offset;
      }
    },
    handleSeeked() {
      this.clipStartTime = this.getPlayerTime();
      this.isDragging = false;
    },
    resetErrors() {
      this.otherError = false;
      this.name.length && this.displayError(false);
    },
    rewindOn(time) {
      this.playPartAbortCb?.();
      const newTime = this.getPlayerTime() - time;
      this.player?.[PLAYER_ACTIONS.seek](newTime);
    },
    flashForwardOn(time) {
      this.playPartAbortCb?.();
      const newTime = this.getPlayerTime() + time;
      this.player?.[PLAYER_ACTIONS.seek](newTime);
    },
  },
  watch: {
    name() {
      this.resetErrors();
    },
    startTime() {
      this.resetErrors();
    },
    selectedDuration() {
      this.resetErrors();
    },
    canCut(bool) {
      this.resetErrors();
      this.showDisabledError && this.displayError(!bool);
    },
    videoPlay() {
      this.updateDuration();
    },
  },
  computed: {
    ...mapState({
      settings: (state) => state.channel.settings,
    }),
    clipDuration() {
      return this.settings?.clipping?.duration || {min: 15, max: 120};
    },
    clipTags() {
      return this.settings?.clipping?.tags || [];
    },
    visibleDuration() {
      return this.clipDuration?.max + 60;
    },
    skipButtons() {
      return [
        ...this.$options.rewindButtons.map((button) => (
          {...button,
            action: () => this.rewindOn(button.time),
            key: `rewindButtons_${button.time}`,
            title: `rewind on ${button.time}s`,
          }
        )),
        ...this.$options.flashForwardButtons.map((button) => (
          {...button,
            action: () => this.flashForwardOn(button.time),
            key: `flashForwardButtons_${button.time}`,
            title: `flash forward on ${button.time}s`,
          }
        )),
      ];
    },
    buttonTags() {
      const sportType = this.sportType?.toLowerCase();

      return this.clipTags[sportType] ||
        this.clipTags['default'] ||
        [
          'Shot',
          'Save',
          'Goal',
          'Highlight',
          'Faceoff',
          'Hit',
          'Penalty',
          'Other',
        ];
    },
    canCut() {
      return this.name.length && this.correctDuration && this.tag;
    },
    correctDuration() {
      return this.endTime <= this.duration;
    },
    endTime() {
      return this.clipStartTime + this.selectedDuration;
    },
  },
  mounted() {
    this.player = this.playerInterface;
    this.player?.[PLAYER_ACTIONS.play](false);
    this.updateDuration();
    const position = this.getPlayerTime() || 0;
    const maxTimeStart = this.duration - this.clipDuration.min;

    this.playingTime = position;

    if (position < maxTimeStart) {
      this.startTime = position;
      this.clipStartTime = position;
    } else {
      this.startTime = maxTimeStart;
      this.clipStartTime = maxTimeStart;
      this.player?.[PLAYER_ACTIONS.seek](maxTimeStart);
    }
    const removeSeekedListener = this.player?.[PLAYER_EVENTS.seeked](this.handleSeeked);
    const removeSeekListener = this.player?.[PLAYER_EVENTS.seek](this.startSeekPlayer);
    const removeTimeListener = this.player?.[PLAYER_EVENTS.time](({currentTime}) => {
      this.playingTime = currentTime;
    });

    this.$on('hook:beforeDestroy', () => {
      removeSeekedListener();
      removeSeekListener();
      removeTimeListener();
      this.player = null;
    });
  },
};
</script>

<style scoped lang="scss">
@import 'src/assets/css/mixins';

.created-clips {
  max-width: 100vw;
}

.clip-wrapper {
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  width: 30vw;
  font-weight: normal;

  @include media-max(lg) {
    padding-right: 15px;
    padding-left: 15px;
    width: 100%;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--settings-body-text-color);
    text-align: left;
  }

  label {
    margin-bottom: 0;
  }

  input {
    width: auto;
  }
}

.section-clip-title {
  display: flex;
  gap: 1rem;
  align-items: center;

  .placeholder-text {
    font-weight: normal;
    text-transform: none;
    color: var(--settings-body-text-color-50);
  }
}

.quick-names {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  font-size: 0.75rem;

  .button {
    --border: var(--theme-link-accent);

    &.selected {
      background: var(--theme-link-accent);
      --text: var(--settings-body-background);
    }
  }
}

.skip-buttons {
  .button {
    --border: var(--theme-link-accent);
  }
}

.clip-name {
  display: flex;
  gap: 1rem;
  align-items: center;

  @include media-max(xs) {
    flex-wrap: wrap;
  }
}

.clip-error {
  text-align: left;
  margin-top: 1rem;
  color: var(--theme-error);
}

.clip-ruler {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}
</style>
